import React from "react";
import { Link } from "react-router-dom";
import { Cpu, ChevronRight, Sun, Battery, Plug, Router } from "lucide-react";

/**
 * CUST-EXP-001A · Preview of the authoritative "My System" projection.
 * Reuses the same equipment shape served by GET /projects/{id}/equipment
 * (SYS-001). Deep-links to /system for full detail.
 */

function iconFor(type) {
    const t = (type || "").toLowerCase();
    if (t.includes("panel") || t.includes("solar") || t.includes("pv")) return Sun;
    if (t.includes("battery") || t.includes("storage")) return Battery;
    if (t.includes("ev") || t.includes("charger") || t.includes("wallbox")) return Plug;
    if (t.includes("gateway") || t.includes("monitor") || t.includes("meter")) return Router;
    return Cpu;
}

export default function SystemPreview({ items = [], loading = false }) {
    const shown = items.slice(0, 4);
    return (
        <div className="pe-card p-5 md:p-6" data-testid="system-preview">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <span className="pe-icon-tile w-8 h-8"><Cpu size={14} strokeWidth={1.75} /></span>
                    <div>
                        <div className="text-[11px] tracking-[0.14em] uppercase text-[#4B5565]">My System</div>
                        <div className="text-base text-[#131B2F] font-medium" data-testid="system-preview-count">
                            {loading ? "Loading…" : `${items.length} item${items.length === 1 ? "" : "s"}`}
                        </div>
                    </div>
                </div>
                <Link
                    to="/system"
                    data-testid="system-preview-view"
                    className="text-sm text-[#3E9E2E] hover:text-[#2E7E22] font-medium inline-flex items-center gap-1"
                >
                    Open <ChevronRight size={14} />
                </Link>
            </div>

            {loading ? (
                <div className="text-sm text-[#4B5565]">Loading your equipment…</div>
            ) : shown.length === 0 ? (
                <div className="text-sm text-[#4B5565]" data-testid="system-preview-empty">
                    Your installer will record equipment here as it is fitted.
                </div>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {shown.map((eq) => {
                        const Icon = iconFor(eq.equipment_type || eq.type);
                        const label = eq.display_name || eq.model || eq.equipment_type || "Equipment";
                        return (
                            <li
                                key={eq.id}
                                data-testid={`system-preview-item-${eq.id}`}
                                className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E7EB] bg-[#FCFCFD] hover:bg-[#F5F5F7] transition-colors"
                            >
                                <span className="w-9 h-9 rounded-lg bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center shrink-0">
                                    <Icon size={16} strokeWidth={1.75} />
                                </span>
                                <div className="min-w-0">
                                    <div className="text-sm text-[#131B2F] font-medium truncate">{label}</div>
                                    <div className="text-xs text-[#4B5565] truncate">
                                        {eq.equipment_type || "Equipment"}
                                        {eq.manufacturer ? ` · ${eq.manufacturer}` : ""}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
